import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { Send, Check, Briefcase, Award, HeartHandshake, Rocket, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useLang } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const easeOut = [0.16, 1, 0.3, 1];
const perkIcons = [Rocket, Award, HeartHandshake, Briefcase];

export default function Careers() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience_years: "",
    location: "",
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!form.name || !form.email || !form.position || !form.message) {
      toast.error(lang === "es" ? "Completa los campos requeridos" : "Please fill all required fields");
      return;
    }
    if (!form.consent) {
      toast.error(
        lang === "es"
          ? "Debes aceptar el Aviso de Privacidad para continuar."
          : "Please accept the Privacy Notice to continue."
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        position: form.position,
        experience_years: form.experience_years ? Number(form.experience_years) : null,
        location: form.location || null,
        message: form.message,
        consent: form.consent,
        language: lang,
      };
      await axios.post(`${API}/careers`, payload);
      toast.success(t.careers.success);
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        position: "",
        experience_years: "",
        location: "",
        message: "",
        consent: false,
      });
    } catch (err) {
      console.error(err);
      toast.error(t.careers.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div className="lg:max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{t.careers.eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.5rem)] text-white tracking-tight leading-[1.05]">
              {t.careers.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] max-w-2xl text-base leading-relaxed">
              {t.careers.subtitle}
            </p>
          </div>
        </div>

        {/* Perks row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mb-16">
          {t.careers.perks.map((p, i) => {
            const PIcon = perkIcons[i] || Briefcase;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: easeOut }}
                data-testid={`career-perk-${i}`}
                className="bg-[#0F0F11] p-6 lg:p-8 flex flex-col"
              >
                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center mb-4">
                  <PIcon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
                </div>
                <div className="font-display font-semibold text-white text-sm lg:text-base tracking-tight">
                  {p.k}
                </div>
                <div className="mt-2 text-xs text-[#8B939C] leading-relaxed">{p.v}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: openings list */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: easeOut }}
            className="lg:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="overline">{t.careers.openings_title}</span>
            </div>
            <ul className="divide-y divide-white/5 border border-white/5 bg-[#0F0F11]">
              {t.careers.openings.map((o, i) => (
                <li
                  key={i}
                  data-testid={`career-opening-${i}`}
                  className="group px-5 py-5 flex items-center justify-between gap-4 hover:bg-[#121214] transition-colors duration-300"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-white text-sm lg:text-base">
                      {o.role}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-[#8B939C]">
                      <span className="text-[#D4AF37]">{o.area}</span>
                      <span className="w-1 h-1 bg-[#8B939C] rounded-full" />
                      <span>{o.loc}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => update("position", o.role)}
                    data-testid={`career-opening-apply-${i}`}
                    className="flex-shrink-0 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#8B939C] hover:text-[#D4AF37] transition-colors duration-300"
                    type="button"
                  >
                    {lang === "es" ? "Aplicar" : "Apply"}
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:text-[#D4AF37]" />
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[#8B939C]">
              {t.careers.fields.cv_note}
            </div>
          </motion.div>

          {/* Right: application form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: easeOut, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <div className="relative border border-white/10 bg-[#0F0F11] p-8 lg:p-10">
              {submitted ? (
                <div
                  data-testid="career-success"
                  className="min-h-[420px] flex flex-col items-center justify-center text-center"
                >
                  <div className="w-14 h-14 border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center">
                    <Check className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="overline mt-6">
                    {lang === "es" ? "Aplicación recibida" : "Application received"}
                  </div>
                  <div className="font-display font-bold text-white text-2xl mt-4 max-w-md">
                    {t.careers.success}
                  </div>
                  <button
                    data-testid="career-reset"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-[#D4AF37] text-[11px] font-bold uppercase tracking-[0.2em] hover:text-[#F0C84B] transition-colors"
                  >
                    {lang === "es" ? "Enviar otra aplicación" : "Submit another application"}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="career-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FloatingInput
                      testid="career-name"
                      label={t.careers.fields.name}
                      value={form.name}
                      onChange={(v) => update("name", v)}
                      required
                    />
                    <FloatingInput
                      testid="career-email"
                      label={t.careers.fields.email}
                      type="email"
                      value={form.email}
                      onChange={(v) => update("email", v)}
                      required
                    />
                    <FloatingInput
                      testid="career-phone"
                      label={t.careers.fields.phone}
                      value={form.phone}
                      onChange={(v) => update("phone", v)}
                    />
                    <FloatingInput
                      testid="career-experience"
                      label={t.careers.fields.experience}
                      type="number"
                      value={form.experience_years}
                      onChange={(v) => update("experience_years", v)}
                    />
                    <div className="flex flex-col sm:col-span-2">
                      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
                        {t.careers.fields.position} *
                      </label>
                      <Select
                        value={form.position}
                        onValueChange={(v) => update("position", v)}
                      >
                        <SelectTrigger
                          data-testid="career-position"
                          className="bg-transparent border-0 border-b border-white/15 rounded-none text-white px-0 focus:ring-0 focus:border-[#D4AF37] h-10 hover:border-white/30 transition-colors"
                        >
                          <SelectValue placeholder={t.careers.fields.position_ph} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121214] border border-white/10 text-white rounded-none">
                          {t.careers.openings.map((o) => (
                            <SelectItem
                              key={o.role}
                              value={o.role}
                              className="text-white focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                            >
                              {o.role}
                            </SelectItem>
                          ))}
                          <SelectItem
                            value={lang === "es" ? "Otro perfil" : "Other profile"}
                            className="text-white focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                          >
                            {lang === "es" ? "Otro perfil" : "Other profile"}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <FloatingInput
                      testid="career-location"
                      label={t.careers.fields.location}
                      value={form.location}
                      onChange={(v) => update("location", v)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
                      {t.careers.fields.message} *
                    </label>
                    <textarea
                      data-testid="career-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder={t.careers.fields.message_ph}
                      required
                      rows={4}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none text-white text-sm px-0 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-[#3f3f46] resize-none transition-colors hover:border-white/30"
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer select-none group">
                    <Checkbox
                      data-testid="career-consent"
                      checked={form.consent}
                      onCheckedChange={(v) => update("consent", !!v)}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] data-[state=checked]:text-black rounded-none"
                    />
                    <span className="text-[11px] leading-relaxed text-[#A1A1AA] group-hover:text-white transition-colors">
                      {t.careers.fields.consent}{" "}
                      <Link
                        to="/privacidad"
                        target="_blank"
                        className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F0C84B]"
                      >
                        {lang === "es" ? "Leer Aviso" : "Read Notice"}
                      </Link>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="career-submit"
                    className="group w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? t.careers.submitting : t.careers.submit}
                    {!loading && (
                      <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function FloatingInput({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
        {label}
        {required && " *"}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="bg-transparent border-0 border-b border-white/15 rounded-none text-white text-sm px-0 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-[#3f3f46] transition-colors hover:border-white/30"
      />
    </div>
  );
}
